import { get, writable } from 'svelte/store';

import { getPendingUnitTransfers } from 'src/services/iotu/house-management';
import { user_data } from 'src/stores';
import type { PendingUnitTransfer, UnitTransferSocketAction } from 'src/types/UnitTransfer';

type PendingUnitTransfersState = {
	outgoing: PendingUnitTransfer[];
	incoming: PendingUnitTransfer[];
};

const initialState: PendingUnitTransfersState = {
	outgoing: [],
	incoming: [],
};

const { subscribe, set, update } = writable<PendingUnitTransfersState>(initialState);

/** Session cache of `get_pending_unit_transfers`. */
export const pendingUnitTransfers = { subscribe };

export async function refreshPendingUnitTransfers(): Promise<void> {
	try {
		const response = await getPendingUnitTransfers();
		if (!response?.isok) return;

		set({
			outgoing: response.data?.outgoing ?? response.outgoing ?? [],
			incoming: response.data?.incoming ?? response.incoming ?? [],
		});
	} catch (error) {
		console.error('[pendingUnitTransfers] failed to refresh', error);
	}
}

export function clearPendingUnitTransfers(): void {
	set(initialState);
}

/** Optimistic pending-list update from UNIT_TRANSFER socket (`expired` is owner-only). */
export function applyUnitTransferSocketEvent(data: {
	action?: UnitTransferSocketAction;
	unit_id?: number;
	from_email?: string;
	to_email?: string;
	name?: string;
}): void {
	const unitId = data.unit_id;
	if (unitId == null) return;

	const myEmail = get(user_data)?.email?.toLowerCase() ?? '';
	const fromEmail = data.from_email?.toLowerCase() ?? '';
	const toEmail = data.to_email?.toLowerCase() ?? '';
	const action = data.action ?? '';

	update((state) => {
		const remove = (): PendingUnitTransfersState => ({
			outgoing: state.outgoing.filter((item) => item.unit_id !== unitId),
			incoming: state.incoming.filter((item) => item.unit_id !== unitId),
		});

		if (
			action === 'cancelled' ||
			action === 'declined' ||
			action === 'accepted' ||
			action === 'expired'
		) {
			return remove();
		}

		if (action !== 'offered') return state;

		const next = remove();
		const row: PendingUnitTransfer = {
			unit_id: unitId,
			name: data.name ?? String(unitId),
			to_email: data.to_email,
			from_email: data.from_email,
		};

		if (myEmail && myEmail === fromEmail) next.outgoing = [...next.outgoing, row];
		if (myEmail && myEmail === toEmail) next.incoming = [...next.incoming, row];

		return next;
	});
}
